import { Menu as MenuIcon } from '@mui/icons-material';
import { AppBar, Avatar, Box, Button, CircularProgress, Drawer, FormControlLabel, IconButton, Menu, MenuItem, Stack, Switch, Tab, Tabs, Typography, useTheme } from '@mui/material';
import React, { useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import iconHome from '../asset/icon-home.png';
import iconLesson from '../asset/icon-lesson.png';
import iconLogout from '../asset/icon-logout.png';
import iconPronounce from '../asset/icon-pronounce-blue.png';
import iconSelfStudy from '../asset/icon-self-study.png';
import iconLeaderboard from '../asset/leaderboard-icon-blue.png';
import iconSettings from '../asset/settings-outline-large-blue.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext, { AppBarMenuIndex } from '../context/AppBarContext';
import { isJwtLoggedIn } from '../util/session';

import AddToHomeScreenRoundedIcon from '@mui/icons-material/AddToHomeScreenRounded';
import AppleIcon from '@mui/icons-material/Apple';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';

import { ChineseTeacher, IUser, UserApi } from '../api/UserApi';
import { AudioApi } from '../api/AudioApi';

import { CourseOfferingApi, ICourseOffering } from '../api/CourseOfferingApi';
import female_avatar_1 from "../asset/female_avatar_1.png";
import female_avatar_2 from "../asset/female_avatar_2.png";
import male_avatar from "../asset/male_avatar.png";


export default function TopMenuBar() {

    const history = useHistory();

    const isTouchDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && window.matchMedia("(pointer: coarse)").matches;

    const { menuIndex, setMenuIndex, setErrorMessage, isLoggedIn, setIsLoggedIn } = React.useContext(AppBarContext);

    const theme = useTheme();
    const [loginLinkText, setLoginLinkText] = React.useState("Login");
    const [userRole, setUserRole] = React.useState<IUser['role']>();
    const [menuOpen, setMenuOpen] = React.useState(false);

    const tabContainerRef = useRef<HTMLDivElement>(null);

    // Right side - MORE menu
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    type MenuOption = {
        label: string;
        icon: string;
        to: string | { pathname: string; state: { from: { pathname: string } } };
    };

    const [courseOffering, setCourseOffering] = React.useState<ICourseOffering | null>(null);
    const [menuOptionList, setMenuOptionList] = React.useState<MenuOption[]>([]);

    const handleTopMenuIndexChange = (_event: React.SyntheticEvent, index: number) => {
        setMenuIndex(index as AppBarMenuIndex); // cast number to MenuIndex enum
    };

    const handleLoginClick = () => {
        handleMenuClose();
        history.push('/login');
    };
    const handleLogoutClick = () => {
        handleMenuClose();
        localStorage.removeItem('jwt');
        setIsLoggedIn(false);
        setUserRole(undefined);
        history.push('/login');
    };

    React.useEffect(() => {
        setIsLoggedIn(isJwtLoggedIn())
    }, [setIsLoggedIn])

    React.useEffect(() => {
        if (isLoggedIn) {
            CourseOfferingApi.getCurrent()
                .then(response => {
                    // Handle the response
                    setCourseOffering(response.data);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }
    }, [isLoggedIn, history, setErrorMessage])

    React.useEffect(() => {

        const teaching = userRole === 'teacher' || userRole === 'admin';
        const learningPath = teaching ? '/teacher' : '/student/courses';
        const menuOptionList = [
            { label: "Home", icon: iconHome, to: "/" },
            { label: teaching ? "Teaching" : "My courses", icon: iconLesson, to: (isLoggedIn ? learningPath : { pathname: "/login", state: { from: { pathname: "/student/courses" } } }) },
            { label: "Pronunciation", icon: iconPronounce, to: (isLoggedIn ? "/student/pronunciation" : { pathname: "/login", state: { from: { pathname: "/student/pronunciation" } } }) },
            { label: "Leaderboard", icon: iconLeaderboard, to: (isLoggedIn ? "/leaderboard" : { pathname: "/login", state: { from: { pathname: "/leaderboard" } } }) },
            // { label: "Conversation", icon: iconSelfStudy, to: (isLoggedIn ? "/chat" : { pathname: "/login", state: { from: { pathname: "/chat" } } }) },
            // { label: "Office Hours", icon: iconSelfStudy, to: (isLoggedIn ? "/office_hour" : { pathname: "/login", state: { from: { pathname: "/office_hour" } } }) }
        ]
        if (isLoggedIn && !teaching) {
            menuOptionList.push({ label: 'Legacy lessons', icon: iconLesson, to: '/lesson' });
        }
        if (courseOffering == null || courseOffering.display_conversation_menu) {
            menuOptionList.push({ label: "Conversation", icon: iconSelfStudy, to: (isLoggedIn ? "/chat" : { pathname: "/login", state: { from: { pathname: "/chat" } } }) })
        }
        if (courseOffering == null || courseOffering.display_office_hour_menu) {
            menuOptionList.push({ label: "Office Hours", icon: iconSelfStudy, to: (isLoggedIn ? "/office_hour" : { pathname: "/login", state: { from: { pathname: "/office_hour" } } }) })
        }
        if (isLoggedIn) {
            setLoginLinkText('Logout');
            menuOptionList.push({ label: "Settings", icon: iconSettings, to: teaching ? "/teacher/account" : "/student/settings" })
        } else {
            setLoginLinkText('Login');
        }
        setMenuOptionList(menuOptionList)

    }, [isLoggedIn, courseOffering, userRole])


    /**
     * Handle audio teacher settings
     */
    const [chineseTeacher, setChineseTeacher] = React.useState(localStorage.getItem("teacher"))
    const [isAudioSpeedFast, setIsAudioSpeedFast] = React.useState(true);
    const [isTeacherSaving, setIsTeacherSaving] = React.useState(false);
    const [isAudioSpeedSaving, setIsAudioSpeedSaving] = React.useState(false);

    React.useEffect(() => {
        if (isJwtLoggedIn()) {

            UserApi.get()
                .then(response => {
                    // Handle the response
                    const user: IUser = response.data;
                    if (user) {
                        setUserRole(user.role);
                        if (user.chinese_teacher) {
                            setChineseTeacher(user.chinese_teacher);
                            AudioApi.setSelectedCustomSentenceTeacher(user.chinese_teacher);
                        }
                        if (user.audio_speed_fast !== null && user.audio_speed_fast !== undefined) setIsAudioSpeedFast(Boolean(user.audio_speed_fast));
                    }
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }
    }, [anchorEl, history, isLoggedIn, setErrorMessage]);


    const handleMale = async () => {
        AudioApi.setSelectedCustomSentenceTeacher(ChineseTeacher.Male);
        updateDbTeacher(ChineseTeacher.Male);
    };

    const handleFemale = async () => {
        AudioApi.setSelectedCustomSentenceTeacher(ChineseTeacher.Female);
        updateDbTeacher(ChineseTeacher.Female);
    };
    const handleFemale2 = async () => {
        AudioApi.setSelectedCustomSentenceTeacher(ChineseTeacher.Female2);
        updateDbTeacher(ChineseTeacher.Female2);
    };

    const updateDbTeacher = (teacher: ChineseTeacher) => {
        setIsTeacherSaving(true);
        const updatedUser: IUser = {
            chinese_teacher: teacher,
        };
        // Call the update function
        UserApi.update(updatedUser)
            .then(() => {
                setChineseTeacher(teacher)
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsTeacherSaving(false);
                handleMenuClose();
            });
    };

    const handleAudioSpeedToggle = () => {
        setIsAudioSpeedSaving(true);
        const newValue = !isAudioSpeedFast;
        setIsAudioSpeedFast(newValue);

        const updatedUser: IUser = {
            audio_speed_fast: newValue,
        };
        UserApi.update(updatedUser)
            .then((response) => {
                if (response.status >= 300) {
                    throw new Error("Saving audio speed was not ok statuscode=" + response.status + " " + response.statusText);
                }
                AudioApi.notifyCustomSentencePlaybackSettingsChanged();
                return response.data;
            })
            .catch((err) => {
                setIsAudioSpeedFast(!newValue);
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsAudioSpeedSaving(false);
                handleMenuClose();
            });
    };

    /**
     * Full Screen
     */
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    const handleFullscreenToggle = async () => {
        const el: any = document.documentElement;

        if (!document.fullscreenElement) {
            const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
            if (req) {
                await req.call(el);
                setIsFullscreen(true);
            }
        } else {
            const exit = document.exitFullscreen || (document as any).webkitExitFullscreen || (document as any).mozCancelFullScreen || (document as any).msExitFullscreen;
            if (exit) {
                await exit.call(document);
                setIsFullscreen(false);
            }
        }
        handleMenuClose();
    };

    /**
     * install on home screen
     */

    const AddToHomeScreenMenuItem = () => {
        const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
        const [showAndroidPrompt, setShowAndroidPrompt] = React.useState(false);
        const [showIosInstructions, setShowIosInstructions] = React.useState(false);

        React.useEffect(() => {
            // Android: listen for beforeinstallprompt
            const handleBeforeInstallPrompt = (e: any) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowAndroidPrompt(true);
            };

            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);


            // iOS detection
            const isIos = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
            const isInStandalone = 'standalone' in window.navigator && (window.navigator as any).standalone;
            if (isIos && !isInStandalone) {
                setShowIosInstructions(true);
            }

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }, []);

        const handleInstallClick = async () => {
            if (!deferredPrompt) return;
            deferredPrompt.prompt();
            const result = await deferredPrompt.userChoice;
            if (result.outcome === 'accepted') {
                console.log('User accepted the install prompt');
            }
            setDeferredPrompt(null);
            setShowAndroidPrompt(false);
        };

        if (showAndroidPrompt) {
            return (
                <MenuItem onClick={handleInstallClick} sx={{ gap: 2.5, height: 48, width: 250, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    <AddToHomeScreenRoundedIcon fontSize="small" />
                    Add to Home Screen
                </MenuItem>
            );
        }

        if (showIosInstructions) {
            return (
                <MenuItem disabled sx={{ whiteSpace: 'normal', gap: 2.5, height: 48, width: 250, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    <AppleIcon fontSize="small" />
                    <Typography variant="body2">
                        On iPhone, tap Share, then Add to Home Screen.
                    </Typography>
                </MenuItem>
            );
        }

        return null;
    };


    return (
        <AppBar position="static" elevation={0}
            sx={{
                backgroundColor: theme.palette.background.default,
                borderBottom: '1.5px solid rgba(82, 99, 125, 0.2)',
                padding: '16px 32px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>

            {/* Mobile Menu Button */}
            <IconButton aria-label="Open navigation menu" onClick={() => setMenuOpen(true)} sx={{ display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' } }}>
                <MenuIcon />
            </IconButton>

            {/* Tabs */}
            <Stack direction="row" alignItems="center" sx={{ flex: 1, display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}>
                <div ref={tabContainerRef} style={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    whiteSpace: 'nowrap',
                    flex: 1
                }}>
                    <Tabs value={menuIndex} onChange={handleTopMenuIndexChange} sx={{ display: 'flex' }}>
                        {menuOptionList.map((menuOption, index) => (
                            <Tab key={index}
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={menuOption.icon} alt="" aria-hidden="true" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                                        {menuOption.label}
                                    </div>
                                }
                                component={Link}
                                to={menuOption.to}
                                sx={{ color: theme.palette.primary.main, fontSize: '16px' }}
                            />
                        ))}
                    </Tabs>
                </div>
            </Stack>

            {/* Error Message, replaced by toast popup in App.tsx*/}
            {/* {errorMessage && errorMessage.length > 0 && (
                <Typography variant="body1" sx={{ color: 'red', marginRight: '16px' }}>{errorMessage}</Typography>
            )} */}


            {/* right side MORE menu w/ logout button, or login button */}
            {isLoggedIn ? (<>
                <IconButton aria-label="Open user menu" onClick={handleMenuOpen} sx={{ color: theme.palette.primary.main }}>
                    <MoreVertRoundedIcon />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Stack px={1} py={1}>
                        {/* Teacher Avatar Picker */}
                        <MenuItem disableRipple sx={{ height: 48, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {isTeacherSaving ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Box display="flex" justifyContent="center" gap={2}>
                                    <Avatar
                                        src={male_avatar}
                                        onClick={handleMale}
                                        sx={{
                                            border: chineseTeacher === ChineseTeacher.Male ? '2px solid' : 'none',
                                            borderColor: theme.palette.primary.main,
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Avatar
                                        src={female_avatar_1}
                                        onClick={handleFemale}
                                        sx={{
                                            border: chineseTeacher === ChineseTeacher.Female ? '2px solid' : 'none',
                                            borderColor: theme.palette.primary.main,
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Avatar
                                        src={female_avatar_2}
                                        onClick={handleFemale2}
                                        sx={{
                                            border: chineseTeacher === ChineseTeacher.Female2 ? '2px solid' : 'none',
                                            borderColor: theme.palette.primary.main,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Box>
                            )}
                        </MenuItem>

                        {/* Audio Speed Toggle */}
                        <MenuItem disableRipple sx={{ height: 48, width: 200, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pl: 0 }}>
                            {isAudioSpeedSaving ? (
                                <CircularProgress size={24} />
                            ) : (
                                <FormControlLabel
                                    control={<Switch checked={isAudioSpeedFast} onChange={handleAudioSpeedToggle} size="small" />}
                                    label={
                                        <Typography sx={{ pl: 2 }}>
                                            Audio Speed: {isAudioSpeedFast ? "Fast" : "Slow"}
                                        </Typography>
                                    }
                                    labelPlacement="end"
                                    sx={{ margin: 0, pl: 0, ml: 0, alignItems: 'center', }}
                                />
                            )}
                        </MenuItem>

                        {/* Install on Home screen */}
                        <AddToHomeScreenMenuItem />

                        {/* Fullscreen Toggle */}
                        {isTouchDevice && (
                            <MenuItem disableRipple sx={{ gap: 2.5, height: 48, width: 200, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                onClick={handleFullscreenToggle}
                            >
                                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenRoundedIcon fontSize="small" />}
                                {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                            </MenuItem>
                        )}

                        {/* Send feedback */}
                        <MenuItem
                            component={Link}
                            to="/comment"
                            onClick={handleMenuClose}
                            disableRipple
                            sx={{ gap: 1.5, height: 48, width: 200, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                            <FeedbackOutlinedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} aria-hidden={true} />
                            Send feedback
                        </MenuItem>

                        {/* Logout button */}
                        <MenuItem disableRipple onClick={handleLogoutClick} sx={{ gap: 1, height: 48, width: 200, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <img src={iconLogout} alt="" aria-hidden="true" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                            {loginLinkText}
                        </MenuItem>
                    </Stack>
                </Menu>
            </>) : (
                <Button variant="outlined"
                    onClick={handleLoginClick}
                    sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: 'transparent',
                        },
                        minWidth: '128px',
                        height: '48px'
                    }}>
                    Log In
                </Button>
            )}

            {/* Mobile Drawer for Collapsible Menu */}
            <Drawer
                anchor="left"
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                PaperProps={{ 'data-testid': 'main-nav-drawer-paper' }}
            >
                <Stack direction="column" sx={{ width: 250, padding: 2 }}>
                    {menuOptionList.map((item, index) => (
                        <Button key={index} component={Link} to={item.to} onClick={() => setMenuOpen(false)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontSize: '16px',
                                color: theme.palette.primary.main
                            }}>
                            <img src={item.icon} alt="" aria-hidden="true" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                            {item.label}
                        </Button>
                    ))}
                </Stack>
            </Drawer>
        </AppBar>
    );
}
